import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Divider,
  MenuItem,
  TextField,
  Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { useConnections } from 'api/connectors';
import { useWarehouses } from 'api/warehouse';
import { useCreatePipeline } from 'api/pipelines';
import { extractApiError, hasFieldErrors, normalizeFieldErrors } from 'api/errors';
import ScheduleCronFields, { CUSTOM_PRESET } from 'components/pipelines/ScheduleCronFields';

import { useTranslation } from 'react-i18next';

export default function NewPipelinePage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { data: connections, isLoading: connectionsLoading } = useConnections();
  const { data: warehouses, isLoading: warehousesLoading } = useWarehouses();
  const createPipeline = useCreatePipeline();

  const [name, setName] = useState('');
  const [sourceId, setSourceId] = useState('');
  const [warehouseId, setWarehouseId] = useState('');
  const [schedulePreset, setSchedulePreset] = useState('');
  const [customCron, setCustomCron] = useState('');
  const [formErrors, setFormErrors] = useState({});
  const [submitError, setSubmitError] = useState(null);

  const scheduleCron = schedulePreset === CUSTOM_PRESET ? customCron.trim() : schedulePreset;

  const validate = () => {
    const errors = {};
    if (!name.trim()) errors.name = t('data.pipelineNameIsRequired');
    if (!sourceId) errors.source_connection_id = t('data.sourceConnectionIsRequired');
    if (!warehouseId) errors.warehouse_id = t('data.destinationWarehouseIsRequired');
    if (schedulePreset === CUSTOM_PRESET && !customCron.trim()) {
      errors.schedule_cron = t('data.enterACronExpressionOrChooseManualOnly2');
    }
    return errors;
  };

  const handleSubmit = async () => {
    const errors = validate();
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }
    setFormErrors({});
    setSubmitError(null);

    const payload = {
      name: name.trim(),
      source_connection_id: sourceId,
      warehouse_id: warehouseId,
      schedule_cron: scheduleCron,
      is_active: true,
    };

    try {
      const pipeline = await createPipeline.mutateAsync(payload);
      navigate(`/console/pipelines/${pipeline.public_id}`);
    } catch (err) {
      if (hasFieldErrors(err)) {
        setFormErrors(normalizeFieldErrors(err.response.data));
      } else {
        setSubmitError(extractApiError(err, t('data.failedToCreatePipelinePleaseTryAgain')));
      }
    }
  };

  const activeConnections = connections?.filter((c) => c.status === 'active') || [];
  const activeWarehouses = warehouses?.filter((w) => w.is_active) || [];
  const loading = connectionsLoading || warehousesLoading;

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3 }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/console/pipelines')}
          size="small"
          color="inherit"
        >{t('data.back')}</Button>
        <Divider orientation="vertical" flexItem />
        <AccountTreeIcon color="primary" />
        <Typography variant="h5" fontWeight={700}>{t('data.newPipeline')}</Typography>
      </Box>

      <Box sx={{ maxWidth: 560 }}>
        {submitError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {submitError}
          </Alert>
        )}

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress />
          </Box>
        ) : activeConnections.length < 1 || activeWarehouses.length < 1 ? (
          <Alert severity="warning" sx={{ mb: 2 }}>{t('data.youNeedAtLeastOneActiveConnectionAndOneWarehouseTo')}{' '}
            <Button size="small" onClick={() => navigate('/console/connectors')}>{t('data.connections')}</Button>
            <Button size="small" onClick={() => navigate('/console/pipelines?tab=storage')}>{t('data.workspaceData.storageSettings')}</Button>
          </Alert>
        ) : (
          <>
            {/* Name */}
            <TextField
              label={t('data.pipelineName')}
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
              fullWidth
              sx={{ mb: 2 }}
              error={!!formErrors.name}
              helperText={formErrors.name || t('data.aUniqueNameForThisPipeline')}
            />

            <Divider sx={{ mb: 2 }} />
            <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1.5 }}>{t('data.dataFlow')}</Typography>

            {/* Source */}
            <TextField
              select
              label={t('data.sourceConnection')}
              value={sourceId}
              onChange={(e) => setSourceId(e.target.value)}
              required
              fullWidth
              sx={{ mb: 2 }}
              error={!!formErrors.source_connection_id}
              helperText={formErrors.source_connection_id || t('data.whereToExtractDataFrom')}
            >
              {activeConnections.map((c) => (
                <MenuItem key={c.public_id} value={c.public_id}>
                  {c.name}
                  <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                    ({c.connector_type?.name})
                  </Typography>
                </MenuItem>
              ))}
            </TextField>

            {/* Flow arrow */}
            <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
              <ArrowForwardIcon color="action" sx={{ transform: 'rotate(90deg)' }} />
            </Box>

            {/* Destination warehouse */}
            <TextField
              select
              label={t('data.destinationWarehouse2')}
              value={warehouseId}
              onChange={(e) => setWarehouseId(e.target.value)}
              required
              fullWidth
              sx={{ mb: 2 }}
              error={!!formErrors.warehouse_id}
              helperText={formErrors.warehouse_id || t('data.whereToLoadTheDataYourWarehouse')}
            >
              {activeWarehouses.map((w) => (
                <MenuItem key={w.public_id} value={w.public_id}>
                  {w.name}
                  <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                    ({w.engine_display})
                  </Typography>
                </MenuItem>
              ))}
            </TextField>

            <Divider sx={{ mb: 2 }} />
            <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1.5 }}>{t('data.scheduleOptional')}</Typography>

            {/* Schedule preset + custom cron */}
            <ScheduleCronFields
              preset={schedulePreset}
              customCron={customCron}
              onPresetChange={(v) => {
                setSchedulePreset(v);
                setFormErrors((prev) => ({ ...prev, schedule_cron: undefined }));
              }}
              onCustomCronChange={(v) => {
                setCustomCron(v);
                setFormErrors((prev) => ({ ...prev, schedule_cron: undefined }));
              }}
              cronError={formErrors.schedule_cron}
            />

            <Box sx={{ mt: 1 }}>
              <Button
                variant="contained"
                onClick={handleSubmit}
                disabled={createPipeline.isPending}
                startIcon={
                  createPipeline.isPending ? (
                    <CircularProgress size={16} color="inherit" />
                  ) : null
                }
              >
                {createPipeline.isPending ? t('data.creating') : t('data.createPipeline')}
              </Button>
            </Box>
          </>
        )}
      </Box>
    </Box>
  );
}
